import { Box, Button, Paper, Typography } from '@mui/material'

import isEqual from 'lodash/isEqual'
import {
  CaseCategory,
  mapDraftToTaskBody,
  mapResponseTasksToDrafts,
  usePersonCaseCategories,
  useTaskCollectionPreviewMutation,
  useUpdateCase,
} from './CaseService'
import {
  ProvidersCasesPartialUpdateResponse,
  ProvidersCasesRetrieveResponse,
} from '@fireflyhealth/api'
import { useSelector } from 'react-redux'
import { useEffect, useState } from 'react'

import { TaskFormDraft } from '~/entities/task'
import { Loader } from '~/shared/ui'
import { PatientUser } from '~/shared/types'
import { format } from 'date-fns'
import { ISO_DATE_FORMAT, formatApiDate, setDueDateToEOD } from '~/shared/lib/date'
import CaseCategorySelect from './CaseCategorySelect'
import invariant from 'tiny-invariant'
import { haveChanged } from '~/shared/lib/haveChanged'

export interface CaseEditorProps {
  patient: PatientUser
  handleClose: () => void
  case?: ProvidersCasesRetrieveResponse
  onAfterSave?: (c: ProvidersCasesPartialUpdateResponse) => void
}

// Whether the user has been working on the tasks, so templated tasks must not overwrite them.
const hasEditedTasks = (tasks: TaskFormDraft[]) => tasks.length > 1 || !!tasks[0]?.title

const CaseForm = (props: CaseEditorProps) => {
  const { patient, handleClose, onAfterSave } = props
  const me = useSelector(state => state.me)

  const { result: categories, isLoading: isListingCaseCategories } = usePersonCaseCategories(
    patient.person.id,
  )

  const { isPending: isUpdatingPatientCase, mutate: updatePatientCase } = useUpdateCase()
  const taskCollectionPreviewMutation = useTaskCollectionPreviewMutation()
  const { mutate: previewTaskCollection } = taskCollectionPreviewMutation

  const [category, setCategory] = useState<CaseCategory | null>(
    categories.find(c => c.id === props.case?.category) ?? null,
  )
  const [description, setDescription] = useState<string>(props.case?.description || '')
  const [notes, setNotes] = useState<string>(props.case?.notes || '')

  const isNew: boolean = props.case?.id === undefined

  // If its a new case, then set the due date as today.
  // If its a old case, then format the dueDate only if its not null.
  // dueDate is store as data time with timezone, so format and retrive only date.
  const caseDueDate = formatApiDate(props.case?.due_date, ISO_DATE_FORMAT) ?? ''
  const dueDate = isNew ? format(new Date(), ISO_DATE_FORMAT) : caseDueDate
  const ownerGroup = props.case?.owner_group ?? me.assignee_group?.id

  const caseTaskDrafts: TaskFormDraft[] = props.case?.tasks
    ? mapResponseTasksToDrafts(props.case.tasks)
    : []

  const [tasks, setTasks] = useState<TaskFormDraft[]>(caseTaskDrafts)

  // Replaces the tasks with the Case Category's templated tasks.
  const applyTaskPreviews = (taskPreviews: TaskFormDraft[]) =>
    setTasks(current => {
      if (hasEditedTasks(current)) return current
      const overwritten = current.map((task, index) =>
        index < taskPreviews.length ? { ...task, ...taskPreviews[index] } : task,
      )
      return [...overwritten, ...taskPreviews.slice(current.length)]
    })

  // A category change resets an empty description and notes to the category's own.
  const categoryDefaultsTrigger = [
    category,
    me.assignee_group,
    patient.id,
    props.case?.description,
    props.case?.notes,
  ]
  const [lastCategoryDefaultsTrigger, setLastCategoryDefaultsTrigger] = useState<unknown[]>()
  if (haveChanged(lastCategoryDefaultsTrigger, categoryDefaultsTrigger)) {
    setLastCategoryDefaultsTrigger(categoryDefaultsTrigger)
    if (!props.case?.description) setDescription(category?.description || '')
    if (!props.case?.notes) setNotes(category?.notes || '')
  }

  // Load any Tasks templates linked to the Case Category
  useEffect(() => {
    if (!category?.task_collection_id) return
    // Throws if this runs before the `me` slice has loaded.
    invariant(me.assignee_group !== null, 'Expected me.assignee_group to be not null')

    previewTaskCollection(
      {
        caseCategoryId: category.id,
        userId: patient.id,
        meAssigneeGroup: me.assignee_group.id,
      },
      { onSuccess: ({ taskPreviews }) => applyTaskPreviews(taskPreviews) },
    )
  }, [category, me.assignee_group, patient.id, previewTaskCollection])

  const hasAllTasksTitles = () => (tasks ?? []).every(task => task.title != '')
  const isTaskChanged: boolean = !isEqual(props.case ? caseTaskDrafts : undefined, tasks)

  const isCategoryChanged: boolean = (category && props.case?.category !== category.id) || false
  const isDescriptionChanged: boolean = props.case?.description !== description
  const isNotesChanged: boolean = props.case?.notes !== notes
  const isDueDateChanged: boolean =
    (dueDate && formatApiDate(props.case?.due_date, ISO_DATE_FORMAT) !== dueDate) || false
  const isOwnerGroupChanged: boolean =
    (ownerGroup && props.case?.owner_group !== ownerGroup) || false
  const hasUnsavedChanges: boolean =
    isTaskChanged ||
    isCategoryChanged ||
    isDescriptionChanged ||
    isNotesChanged ||
    isDueDateChanged ||
    isOwnerGroupChanged

  const isLoading: boolean = isListingCaseCategories || isUpdatingPatientCase
  const hasDueDate: boolean = dueDate ? true : false
  const hasOwnerGroup: boolean = ownerGroup ? true : false
  const hasCategory: boolean = category ? true : false
  // Has description if it is a new case (old cases or machine-generated cases will not)
  const hasDescription: boolean = isNew ? !!description : true
  const hasTaskTitles: boolean = hasAllTasksTitles()

  // The same editor is used even for care plan and it doesn't have owner and due date. So skipping this check.
  const hasRequiredFields: boolean =
    hasCategory && hasDueDate && hasOwnerGroup && hasDescription && hasTaskTitles

  const handleUpdate = () => {
    if (!props.case) {
      handleClose()
      return
    }
    const relations = (props.case.relations ?? []).map(relation => ({
      content_type: relation.content_type,
      object_id: relation.object_id,
    }))
    updatePatientCase(
      {
        id: props.case.id,
        body: {
          person: patient.person.id,
          category: category ? category.id : props.case.category,
          tasks: tasks.map(task =>
            mapDraftToTaskBody(task, task.due_date ? setDueDateToEOD(task.due_date) : undefined),
          ),
          relations,
          description,
          notes,
          due_date: dueDate != '' ? setDueDateToEOD(dueDate) : undefined,
          owner_group: ownerGroup ?? undefined,
        },
      },
      {
        // Closes only once the server has taken the update; a refusal leaves the editor open
        // with the edits in place.
        onSuccess: updatedCase => {
          onAfterSave?.(updatedCase)
          handleClose()
        },
      },
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ m: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {'Change Case'}
        </Typography>
        <CaseCategorySelect
          categories={categories}
          category={category}
          setCategory={setCategory}
          disabled={!!props.case?.is_category_change_locked}
        />
        {props.case?.is_category_change_locked && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Category can&apos;t be changed for this case.
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 4, display: 'flex', flex: 1, justifyContent: 'flex-end' }}>
        <Box sx={{ ml: 1, mr: 2 }}>
          <Button disabled={isLoading} onClick={() => handleClose()}>
            Cancel
          </Button>
          <Button
            disabled={isLoading || !hasUnsavedChanges || !hasRequiredFields}
            color="primary"
            variant={
              isLoading || !hasUnsavedChanges || !hasRequiredFields ? 'outlined' : 'contained'
            }
            onClick={handleUpdate}
            sx={{
              '& p': {
                fontSize: '1.4rem',
              },
              '&.MuiButton-containedSizeMedium': {
                borderRadius: '24px',
              },
            }}
          >
            <Typography>{'Change Case'}</Typography>
            {isLoading && <Loader inline={true} />}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

const CaseEditor = (props: CaseEditorProps) => (
  <Paper style={{ maxHeight: '90vh', overflow: 'auto' }}>
    <CaseForm {...props} />
  </Paper>
)

export default CaseEditor
